import { useEffect, useMemo, useState } from 'react';
import { useContexteFront } from './lib/front';
import { Contexte } from './composants/Contexte';
import { Todo } from './composants/Todo';
import { Relances } from './composants/Relances';
import { Prospects } from './composants/Prospects';
import { Recherche } from './composants/Recherche';
import { BandeauDemo } from './composants/BandeauDemo';
import { Squelette } from './composants/ui';

type Onglet = 'contexte' | 'todo' | 'relances' | 'prospects' | 'recherche';

/**
 * La recherche est le lot 3 du contrat : l'endpoint n'est pas servi en v1.
 * L'onglet reste donc masqué par défaut, et s'active au build une fois que le
 * pipeline répond sur `/recherche` — ou pour une démonstration.
 */
const RECHERCHE_ACTIVE = import.meta.env.VITE_RECHERCHE === 'true';

const ONGLETS: Array<{ cle: Onglet; libelle: string }> = [
  { cle: 'contexte', libelle: 'Contexte' },
  { cle: 'todo', libelle: 'À faire' },
  { cle: 'relances', libelle: 'Relances' },
  { cle: 'prospects', libelle: 'Hors référentiel' },
  ...(RECHERCHE_ACTIVE ? [{ cle: 'recherche' as const, libelle: 'Recherche' }] : []),
];

export function App() {
  const contexteFront = useContexteFront();

  const cnvId = useMemo(
    () =>
      contexteFront?.type === 'singleConversation'
        ? contexteFront.conversation?.id
        : undefined,
    [contexteFront],
  );

  const teammateId = contexteFront?.teammate?.id;

  const [onglet, setOnglet] = useState<Onglet>('contexte');
  const [choisiParUtilisateur, setChoisiParUtilisateur] = useState(false);

  // Sans conversation ouverte, l'onglet « Contexte » n'a rien à montrer :
  // on bascule sur la to-do — sauf si l'utilisateur a choisi lui-même.
  useEffect(() => {
    if (choisiParUtilisateur) return;
    setOnglet(cnvId ? 'contexte' : 'todo');
  }, [cnvId, choisiParUtilisateur]);

  if (!contexteFront) {
    return (
      <div className="app">
        <Squelette lignes={4} />
      </div>
    );
  }

  return (
    <div className="app">
      <BandeauDemo />

      <nav className="onglets" role="tablist" aria-label="Sections du plugin">
        {ONGLETS.map((element) => (
          <button
            key={element.cle}
            type="button"
            role="tab"
            aria-selected={onglet === element.cle}
            className={`onglet${onglet === element.cle ? ' onglet--actif' : ''}`}
            onClick={() => {
              setOnglet(element.cle);
              setChoisiParUtilisateur(true);
            }}
          >
            {element.libelle}
          </button>
        ))}
      </nav>

      <main className="contenu">
        {onglet === 'contexte' && (
          <Contexte cnvId={cnvId} contexteFront={contexteFront} />
        )}
        {onglet === 'todo' && <Todo contexteFront={contexteFront} teammateId={teammateId} />}
        {onglet === 'prospects' && <Prospects contexteFront={contexteFront} />}
        {onglet === 'relances' && (
          <Relances
            contexteFront={contexteFront}
            teammateId={teammateId}
            auteur={contexteFront?.teammate?.name}
          />
        )}
        {onglet === 'recherche' && <Recherche contexteFront={contexteFront} />}
      </main>

      <footer className="pied">
        Radar client — ce que vous décidez ici revient au pipeline, rien ne part
        à un client sans vous.
      </footer>
    </div>
  );
}
